import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  Package, 
  Truck, 
  Clock,
  Check,
  CheckCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    const token = localStorage.getItem('nas_customer_token');
    if (!token) {
      navigate('/nas-portal/login');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/nas-customer/notifications?token=${token}`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    const token = localStorage.getItem('nas_customer_token');
    try {
      await axios.post(`${API}/api/nas-customer/notifications/${notificationId}/read?token=${token}`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    const token = localStorage.getItem('nas_customer_token');
    try {
      await axios.post(`${API}/api/nas-customer/notifications/mark-all-read?token=${token}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (title) => {
    if (title.includes('Approved')) return <CheckCircle className="text-green-600" size={20} />;
    if (title.includes('Rejected')) return <XCircle className="text-red-600" size={20} />;
    if (title.includes('Released')) return <Package className="text-blue-600" size={20} />;
    if (title.includes('Delivery')) return <Truck className="text-purple-600" size={20} />;
    if (title.includes('Delivered')) return <Check className="text-green-600" size={20} />;
    return <Bell className="text-slate-600" size={20} />;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="text-green-600" size={24} />
          <h1 className="text-xl font-bold text-slate-800">Notifications</h1>
          {unreadCount > 0 && (
            <Badge className="bg-green-600">{unreadCount} new</Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={markAllAsRead}
            className="text-green-600 border-green-200"
          >
            <CheckCheck size={16} className="mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Bell size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No notifications yet</p>
            <p className="text-slate-400 text-sm mt-1">
              You'll receive updates here when your orders are processed
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <Card 
              key={notification.id}
              className={cn(
                "bg-white cursor-pointer transition-all hover:shadow-md",
                !notification.read && "border-l-4 border-l-green-500 bg-green-50/30"
              )}
              onClick={() => {
                if (!notification.read) {
                  markAsRead(notification.id);
                }
                if (notification.order_id) {
                  navigate('/nas-portal/orders');
                }
              }}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.title)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-slate-800">{notification.title}</p>
                      {!notification.read && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                          New
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-600 text-sm">{notification.message}</p>
                    <p className="text-slate-400 text-xs mt-2 flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(notification.created_at).toLocaleString('en-PH', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerNotifications;
